import { useCallback, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  buyerResearchApi,
  type BuyerResearchBriefDto,
  type BuyerResearchConversationCount,
  type BuyerResearchConversationGuards,
} from "@/api/buyerResearch";
import { extractErrorMessage } from "@/api/client";
import { researchCopy } from "./researchCopy";
import { countIsStale } from "./researchFormat";

/**
 * The free match count, and the guards that come back with it.
 *
 * <p>🔴 <b>ONE COUNT PER BRIEF, OWNED ABOVE EVERY SURFACE THAT READS IT.</b>
 * The count used to live inside {@link ResearchConversation} and be MIRRORED up
 * to the drawer through an `onCountChange` callback — so the same number was
 * held in two places, and the drawer's copy was only ever as fresh as the last
 * callback. That was survivable while the conversation was its only author.
 * It stops being survivable the moment a second surface needs to read it, which
 * is what the stepped brief is: a form whose last step has to state the number
 * the specialist is about to spend records on.</p>
 *
 * <p>🔴 <b>THE HOOK IS CALLED ONCE, BY THE OWNER, AND PASSED DOWN.</b> Calling
 * it in two components is two counts — two vendor reads, two answers, and no
 * way to say which one the button meant. There is no context and no cache
 * behind it deliberately: a count is a free vendor read against a brief the
 * client is holding in memory, and nothing server-side describes it, so there
 * is no query for a cache to key.</p>
 *
 * <p><b>RE6 — the count never gates a render.</b> It arrives a beat after the
 * surface it belongs to, and every consumer is expected to render with
 * `count` still null. Nothing here returns a promise a caller could await.</p>
 */
export interface ResearchCounting {
  /** The count on hand, or null before the first one lands. */
  count: BuyerResearchConversationCount | null;
  /**
   * The brief the count on hand was taken AGAINST, serialised.
   *
   * 🔴 Exposed rather than kept private because staleness is the consumer's
   * question, not this hook's: the conversation strikes the figure through and
   * offers a fresh one, the run control refuses to name it on the button, and
   * the stepped brief shows it on its last step. One fact, three readings.
   */
  countedJson: string | null;
  /** The guards as of the last count or turn — run allowance, re-run window. */
  guards: BuyerResearchConversationGuards | null;
  /** The last count failure, already composed for a specialist. */
  error: string | null;
  /** A count is in flight. */
  isCounting: boolean;
  /**
   * Whether the count on hand still describes `briefJson`.
   *
   * Delegates to {@link countIsStale}, whose own doc asks for exactly one
   * implementation of this comparison. A count taken against nothing is not
   * stale, it is ABSENT — consumers render nothing for that, never a
   * struck-through figure with no figure behind it.
   */
  isStaleFor: (briefJson: string | null) => boolean;
  /** Take a fresh count against this brief. */
  take: (brief: BuyerResearchBriefDto) => void;
  /**
   * Adopt a count some OTHER call already measured, without a second vendor
   * read — a conversational turn carries its own current count, and spending a
   * fourth request on a number the turn already answered is the waste RE4
   * bounds.
   */
  adopt: (
    count: BuyerResearchConversationCount | null,
    forJson: string | null,
  ) => void;
  /** Adopt the guards a turn came back with. */
  adoptGuards: (guards: BuyerResearchConversationGuards) => void;
}

export function useResearchCount(
  buyerLeadId: string,
  initialGuards: BuyerResearchConversationGuards | null,
): ResearchCounting {
  const copy = researchCopy.conversation;

  const [count, setCount] = useState<BuyerResearchConversationCount | null>(null);
  const [countedJson, setCountedJson] = useState<string | null>(null);
  const [guards, setGuards] = useState<BuyerResearchConversationGuards | null>(initialGuards);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    // no-invalidation: a free count writes nothing anywhere — it is a vendor
    // read against a brief the client holds, and no cached query describes it.
    mutationFn: (b: BuyerResearchBriefDto) => buyerResearchApi.countBrief(buyerLeadId, b),
  });

  const adopt = useCallback(
    (next: BuyerResearchConversationCount | null, forJson: string | null) => {
      // 🔴 A null count carries no brief. Keeping the json would leave
      // `isStaleFor` answering about a figure that is not on screen.
      setCount(next);
      setCountedJson(next ? forJson : null);
    },
    [],
  );

  /**
   * Counts the brief it is handed and adopts the answer for THAT brief.
   *
   * <p>The json is captured at call time and stored with the answer, so a count
   * that lands after the specialist has moved on is filed against the query it
   * actually measured rather than against whatever is on screen — which is what
   * keeps the staleness test honest instead of self-confirming.</p>
   */
  const take = useCallback(
    (b: BuyerResearchBriefDto) => {
      const forJson = JSON.stringify(b);
      setError(null);
      mutation.mutate(b, {
        onSuccess: (result) => {
          setGuards(result.guards);
          adopt(result.count, forJson);
        },
        onError: (e: unknown) => setError(copy.countFailed(extractErrorMessage(e))),
      });
    },
    [adopt, copy, mutation],
  );

  const isStaleFor = useCallback(
    (briefJson: string | null) => countIsStale(countedJson, briefJson),
    [countedJson],
  );

  return {
    count,
    countedJson,
    guards,
    error,
    isCounting: mutation.isPending,
    isStaleFor,
    take,
    adopt,
    adoptGuards: setGuards,
  };
}
